import { TabStyles, TextFieldProps, TextFieldWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Filter } from '~commonInfra/utils/Filter';
import { Set } from '~commonInfra/utils/Set';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { RouterErrorType } from '~src/bootstrap/redux/state/RouterErrorType';
import { selectCatalogingOnSiteEnabled } from '~src/catalog/selectors/CatalogSelectors';
import { setCollectionVisibility } from '~src/Collections/actions/SetCollectionVisibilityAction';
import { CollectionVisibility } from '~src/Collections/space/CollectionModels';
import { isUserVisibilityLimited } from '~src/commonInfra/utils/sites/SitesFunctions';
import { IProjectInfo } from '~src/projects/models/IProjectInfo';
import { showRouterError } from '~src/spacesPlatform/actions/RouterErrorActions';
import { InProgressRequestsTracker } from '~src/spacesPlatform/sagas/content/MixedContentSagas';

import { Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { HelpService } from '../../commonInfra/services/HelpService';
import { ListResult } from '../../httpClient/fetchers/ListResult';
import { IGetItemsParams } from '../../httpClient/serverApi/types/IGetItemsParams';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { IFilterClause } from '../../spacesPlatform/models/IFilterClause';
import { GetUsersGroupMembershipRequest, IGetUsersGroupMembershipResult } from '../../userManagement/groups/serverApi/requests/GetUsersGroupMembershipRequest';
import { IGroupMember } from '../../userManagement/groups/serverApi/types/IGroupMember';
import { UserKeys } from '../../userManagement/users/serverApi/types/IUser';
import { EffectivePermissions } from '../requests/EffectivePermissions';
import { ExplicitPermissionsResult, getExplicitPermissionsForAuthorizablesAndProjects, setExplicitPermissionsForAuthorizables } from '../requests/ExplicitPermissions';
import { ISourceAuthorizable } from '../requests/types/GetExplicitPermissionsRequest';
import { AddNewPermissionStyles } from '../styles/AddNewPermissionStyle';
import { PermissionsDialogStyles } from '../styles/PermissionsDialogStyle';
import { PermissionsGridStyles } from '../styles/PermissionsGridStyle';
import { PermissionsLockUnlockStyles } from '../styles/PermissionsLockUnlockStyle';
import { AuthorizableType, isAuthorizableTypeCatalog, isAuthorizableTypeContainer } from '../types/AuthorizableType';
import { Capabilities } from '../types/Capabilities';
import { CapabilitySection, CapabilitySectionsByAuthorizableType } from '../types/CapabilitySections';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { ITypedIds } from '../types/ITypedIds';
import { IPermission, Permission, PermissionReason } from '../types/Permission';
import { PermissionRoleName } from '../types/PermissionRole';
import { LockStatusHelper, LockUnlockPermissionsState, NestedProjectsPermissionsState } from '../utils/LockStatusHelper';
import { PermissionRoleHelper } from '../utils/PermissionRoleHelper';
import { PermissionsEditability, PermissionsEditabilityLogic } from '../utils/PermissionsEditabilityLogic';
import { PermissionsHelper } from '../utils/PermissionsHelper';
import { AuthorizableTypeSelector } from './AuthorizableTypeSelector';
import { PermissionsGrid, PermissionsGridRow } from './PermissionsGrid';
import { PermissionsLockUnlock } from './PermissionsLockUnlock';


export const DatabaseAdditionalMessages = {
  mixedMessage: Messages.lockPermissions_controllingDatabaseUnassigned_mixed,
  multiMessage: Messages.lockPermissions_controllingDatabaseUnassigned_multi,
  singleMessage: Messages.lockPermissions_controllingDatabaseUnassigned_single,
};

export const PERMISSIONS_HELP_TESTID = 'permissions-dialog-help';
export const PERMISSIONS_TABS_TESTID = 'permissions-dialog-tabs';
export const EXPLICIT_PERMISSIONS_TESTID = 'explicit-permissions';
export const EFFECTIVE_PERMISSIONS_TESTID = 'effective-permissions';

export type PermissionsDialogOwnProps = {
  /** Original authorizables when dialog is first opened */
  authorizables: Set<any>;

  /** Type of authorizable this dialog is for */
  authorizableType: AuthorizableType;
};

export type PermissionsDialogSelectorProps = {
  readonly catalogingEnabled: boolean;
  readonly einsteinCopilotForTableauEnabled: boolean;
  readonly isUserVisibilityLimited: boolean;
}

export type PermissionsDialogDispatchProps = {
  showRouterError: (errorType: RouterErrorType) => void;
  setCollectionVisibility: (contentIds: string[], visibility: CollectionVisibility) => void;
}

export type PermissionsDialogProps = PermissionsDialogOwnProps & PermissionsDialogDispatchProps & PermissionsDialogSelectorProps;

export type PermissionsDialogState = {
  authorizables: Set<any>; // original values are passed in as props, but future updates are handled within the component
  editability: PermissionsEditability;
  effectivePermissions: ListResult<IGranteePermissions>;
  explicitPermissions: ListResult<IGranteePermissions>;
  isEditingPermissionRule: boolean;
  ruleSelectedFromSearch: boolean;
  selectedAuthorizableTypeIndex: number;
  sourceAuthorizablesById: _.Dictionary<ISourceAuthorizable>;
};

export class PermissionsDialog extends React.Component<PermissionsDialogProps, PermissionsDialogState> {
  public static readonly PERMISSIONS_SEARCH_TEXTBOX_TESTID: string = 'searchForEffectivePermissionText';
  public readonly InputDebounceMilliseconds = 250;
  private totalAuthorizableTypes: AuthorizableType[] = null;
  private unspecifiedPermissions: _.Dictionary<_.Dictionary<IPermission>> = {};
  public explicitGrid: PermissionsGrid = null;
  public effectiveGrid: PermissionsGrid = null;
  public searchTextBoxRef: HTMLInputElement = null;
  private effectivePermissionsFilterClauses: IFilterClause[] = null;
  private dialogRef: React.RefObject<HTMLDivElement> = React.createRef();
  private capabilitySectionsByAuthorizableType: _.Dictionary<ReadonlyArray<CapabilitySection>> = null;
  private permissionRoleHelper: PermissionRoleHelper = null;

  constructor(props: PermissionsDialogProps) {
    super(props);
    this.setAuthorizableTypes();
    this.setCapabilitySections();
    this.createUnspecifiedPermissions();

    this.state = {
      editability: {
        editable: false,
        nonContainerMessage: '',
      },
      authorizables: this.props.authorizables,
      explicitPermissions: null,
      effectivePermissions: null,
      ruleSelectedFromSearch: false,
      selectedAuthorizableTypeIndex: 0,
      sourceAuthorizablesById: {},
      isEditingPermissionRule: false,
    };
  }

  public componentDidMount() {
    this.updateExplicitPermissionsAndEditability();
  }

  public componentDidUpdate(prevProps: PermissionsDialogProps, prevState: PermissionsDialogState, snapshot: any) {
    const dialogRef = this.dialogRef.current;
    if (snapshot !== null && dialogRef !== null && this.state.effectivePermissions !== null) {
      dialogRef.parentElement.scrollTop = snapshot;
    }
  }

  public getSnapshotBeforeUpdate(prevProps: PermissionsDialogProps, prevState: PermissionsDialogState) {
    // On smaller screens, the PermissionsDialog might have to scroll vertically. Any state change (such as getting the
    // next page of effective permissions) triggers a rerender, which causes the scroll position of the dialog to get
    // reset to the top. However, we wish to persist scroll position even after state changes, and returning the scroll
    // position value from getSnapshotBeforeUpdate and setting it as the new scroll value in componentDidUpdate is the
    // officially suggested React way to handle this behavior
    const dialogRef = this.dialogRef.current;
    if (dialogRef !== null) return dialogRef.parentElement.scrollTop;
    return null;
  }

  private setAuthorizableTypes = (): void => {
    if (this.props.authorizableType === AuthorizableType.PROJECT) {
      this.totalAuthorizableTypes = [
        AuthorizableType.PROJECT,
        AuthorizableType.PROJECT_WORKBOOK_TEMPLATE,
        AuthorizableType.PROJECT_DATASOURCE_TEMPLATE,
      ];
      if (getSessionManagementServiceInstance().flowsOnSiteEnabled()) {
        this.totalAuthorizableTypes.push(AuthorizableType.PROJECT_FLOW_TEMPLATE);
      }
      if (FeatureFlags.publishedConnectionContentManagementEnabled) {
        this.totalAuthorizableTypes.push(AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE);
      }
      if (this.props.catalogingEnabled &&
            getSessionManagementServiceInstance().databasesInProjectsPostMVPEnabled()) {
        this.totalAuthorizableTypes.push(AuthorizableType.PROJECT_DATABASE_TEMPLATE);
        this.totalAuthorizableTypes.push(AuthorizableType.PROJECT_TABLE_TEMPLATE);
      }
    } else if (this.props.authorizableType === AuthorizableType.DATABASE) {
      // Check if the database is in a project, and if so then do not show the Tables tab
      if(!this.isDatabaseInProject()) {
        this.totalAuthorizableTypes = [AuthorizableType.DATABASE, AuthorizableType.DATABASE_TABLE_TEMPLATE];
      }
    }
  };

  private setCapabilitySections = (): void => {
    this.capabilitySectionsByAuthorizableType = CapabilitySectionsByAuthorizableType(
        getSessionManagementServiceInstance().editingFlowsEnabled(),
        FeatureFlags.explainDataPermissionEnabled,
        getSessionManagementServiceInstance().databasesInProjectsPostMVPEnabled(),
        this.getIsOnlineEnvironment(),
        FeatureFlags.allowNonAdminRefreshExtractsEnabled,
        FeatureFlags.analyticsAssistantDashboardQAEnabled,
        this.props.einsteinCopilotForTableauEnabled,
    );
    this.permissionRoleHelper = new PermissionRoleHelper(
        this.capabilitySectionsByAuthorizableType,
        FeatureFlags.analyticsAssistantDashboardQAEnabled,
        this.props.einsteinCopilotForTableauEnabled,
    );
  };

  private getIsOnlineEnvironment(): boolean {
    try {
      return getSessionManagementServiceInstance().isOnlineEnvironment();
    } catch (error) {
      // If session management service is not initialized, default to false (on-premise)
      return false;
    }
  }

  private isSessionSiteOrServerAdmin = (): boolean => {
    return getSessionManagementServiceInstance().isSessionSiteOrServerAdmin();
  };

  private createUnspecifiedPermissions = (): void => {
    (this.totalAuthorizableTypes || [this.props.authorizableType]).forEach(authorizableType => {
      this.unspecifiedPermissions[authorizableType] = PermissionsHelper.IPermissionsFromPermissions(
        this.permissionRoleHelper.getPermissionsForTypeAndRole(authorizableType, PermissionRoleName.None)
      );
    });
    if (this.props.authorizableType === AuthorizableType.PROJECT) {
      this.unspecifiedPermissions[AuthorizableType.PROJECT][Capabilities.projectLeader] = {
        permission: Permission.Unspecified,
        reason: PermissionReason.Unspecified,
      };
    }
  };

  insufficientPermissionsCheck = (result: ExplicitPermissionsResult) => {
    // handle error if user has insufficient permissions to manage permissions
    if (result.errors && _.find(result.errors, { code: CommonErrorCode.CallingUserInsufficientPermissions })) {
      this.props.showRouterError('insufficient-permissions');
    }
  };

  setStateEditability = (sourceAuthorizablesById: _.Dictionary<ISourceAuthorizable>) => {
    // PermissionsEditabilityLogic generates a PermissionsEditability, which contains string values that are rendered to explain if permissions are locked
    const editability: PermissionsEditability = PermissionsEditabilityLogic.getPermissionsEditability(this.state.authorizables, this.props.authorizableType, sourceAuthorizablesById);
    this.setState({ editability, sourceAuthorizablesById, });
  };

  private setMissingCapabilities = (explicitPermissions: ListResult<IGranteePermissions>) => {
    // BE doesn't store unspecified capabilities, so set all missing permissions to Unspecified.
    // This is needed to be able to know if a row in edit mode has been edited or has its original value
    (this.totalAuthorizableTypes || [this.props.authorizableType]).forEach(
      (authorizableType: AuthorizableType) => {
        const capabilities = _.keys(this.permissionRoleHelper.getPermissionsForTypeAndRole(authorizableType, PermissionRoleName.None));
        explicitPermissions.items.forEach((granteePermissions: IGranteePermissions) => {
          if (!_.has(granteePermissions.permissionsByContentType, authorizableType)) {
            granteePermissions.permissionsByContentType[authorizableType] = {};
          }
          const permissionsForContentType: _.Dictionary<IPermission> = granteePermissions.permissionsByContentType[authorizableType];
          capabilities.forEach((capability: string) => {
            if (!_.has(permissionsForContentType, capability)) {
              permissionsForContentType[capability] = {
                permission: Permission.Unspecified,
                reason: PermissionReason.Unspecified,
              };
            }
          });

          // project leader is no longer listed as a project capability, so have to handle it separately
          if (authorizableType === AuthorizableType.PROJECT && !permissionsForContentType[Capabilities.projectLeader]) {
            permissionsForContentType[Capabilities.projectLeader] = {
              permission: Permission.Unspecified,
              reason: PermissionReason.Unspecified,
            };
          }
        });
      }
    );

  };

  private setExplicitPermissions = (explicitPermissions: ListResult<IGranteePermissions>) => {
    explicitPermissions.items.sort(PermissionsHelper.compareGrantees);
    this.setMissingCapabilities(explicitPermissions);
    this.setState({ explicitPermissions });
  };

  private authorizableTypeIdsForProject = (): ITypedIds => {

    // project explicit permissions require datasource id, not data connections id - see @W-15518470
    if (this.props.authorizableType === AuthorizableType.DATASOURCE) {
      const ids = this.state.authorizables.map(authorizable => {
        if (authorizable.contentDetail && authorizable.contentDetail.parent) {
          // this handles the case where the DS is loaded by navigating to it in a project
          return authorizable.contentDetail.parent.id;
        } else if (authorizable.location && authorizable.location.contents && authorizable.location.contents['DATASOURCE'] && authorizable.location.contents['DATASOURCE'][0]) {
          // this handles the case where the DS is loaded directly via Explore/'All Data Sources'
          return authorizable.location.contents['DATASOURCE'][0];
        } else {
          // See comment in IDataSourceInfo.ts about the purpose of idForFavoriting
          const authorizableId = authorizable.idForFavoriting ? authorizable.idForFavoriting : authorizable.id;
          return authorizableId;
        }
    });
      return { type: this.props.authorizableType, ids };
    }
    return { type: this.props.authorizableType, ids: this.state.authorizables.keys() };
  };

  private authorizableTypeIds = (): ITypedIds => {
    return { type: this.props.authorizableType, ids: this.state.authorizables.keys() };
  };

  private updateExplicitPermissionsAndEditability = (): Promise<void | ExplicitPermissionsResult> => {
    // server request to get the explicit permissions list
    return getExplicitPermissionsForAuthorizablesAndProjects(this.authorizableTypeIds(), this.authorizableTypeIdsForProject(), this.props.isUserVisibilityLimited && this.props.authorizableType === AuthorizableType.PROJECT)
      .then((result: ExplicitPermissionsResult) => {
        if (result) {
          this.insufficientPermissionsCheck(result);
          this.setStateEditability(result.sourceAuthorizablesById);
          this.setExplicitPermissions(result.permissionsList);
        }
      }).then(() => {
        if (!this.explicitGrid.state.selectedRowIndex) {
          this.explicitGrid.setSelectedRow();
        }
      });
  };

  private getFilterClausesForEffectivePermissionsOnRowSelection = (selectedExplicitPermissionsRow: PermissionsGridRow):
    IFilterClause[] => {
    // Need an IFilterClause[] here because IGetItemParams requires an ICompositeFilterClause,
    // so even though the array will only contain one filter, we need it to make a composite filter
    let filterClauses: IFilterClause[] = [];

    // add a filter clause for the current group or user
    if (selectedExplicitPermissionsRow.granteeType === PermissionsHelper.GranteeType.User) {
      filterClauses.push(Filter.stringFieldEq('id', selectedExplicitPermissionsRow.granteeId));
    } else if (selectedExplicitPermissionsRow.granteeType === PermissionsHelper.GranteeType.Group) {
      filterClauses.push(Filter.stringArrayFieldHas('groupIds', selectedExplicitPermissionsRow.granteeId));
    } else {
      filterClauses.push(Filter.stringArrayFieldHas('groupSetId', selectedExplicitPermissionsRow.granteeId));
    }
    return filterClauses;
  };

  /**
   * Public - only for testing purpose
   */
  public getFilterClausesForEffectivePermissionsOnSearchTextChange = (searchText: string):
    IFilterClause[] => {
    let filterClauses: IFilterClause[] = [];

    // add a filter clause for the current search text
    if (searchText) {
      filterClauses.push(Filter.search(searchText));
    }
    return filterClauses;
  };

  private updateEffectivePermissions = (filterClauses: IFilterClause[], firstPage: boolean): void => {
    const PAGE_SIZE = 99;
    const previous = this.state.effectivePermissions;
    // Page by USER offset, not by loaded grantee-row count. The effective grid pages through the user
    // list, but makeGranteePermissionsList can return fewer rows than the users fetched (it drops the
    // _system user and grantees whose only capability was the deprecated create_refresh_metrics --
    // W-17590382). So items.length drifts behind the true user offset; using it as the next startIndex
    // would re-request already-seen users and duplicate rows. The user fetch returns a full page while
    // more users remain, so advance by PAGE_SIZE and stop once the previous page reported no moreItems
    // (previous.index carries the last requested user offset -- W-24247091).
    if (!firstPage && previous && !previous.moreItems) {
      return;
    }
    const page = firstPage
      ? { startIndex: 0, maxItems: PAGE_SIZE, }
      : { startIndex: previous.index + PAGE_SIZE, maxItems: PAGE_SIZE, };
    if (firstPage) {
      InProgressRequestsTracker.clear();
    }
    if (InProgressRequestsTracker.isRequestInProgress(page.startIndex)) return;

    const effectivePermissionsFetchParams: IGetItemsParams = {
      filter: Filter.and(filterClauses),
      order: [{ field: UserKeys.displayName, ascending: true }],
      page: page,
    };

    const markRequestComplete = (): void => {
      InProgressRequestsTracker.markRequestComplete(page.startIndex);
    };

    const setEffectivePermissionInState = (updatedEffectivePermissions: ListResult<IGranteePermissions>): void => {
      // updatedEffectivePermissions are already sorted
      if (firstPage) {
        // getting the first page means we are getting effective permissions for a new query,
        // so replace any existing effective permissions
        this.setState({ effectivePermissions: updatedEffectivePermissions, }, () => {
          markRequestComplete();
          // then need to fetch group membership for users in updatedEffectivePermissions
          this.updateUsersGroupMembership(updatedEffectivePermissions);
        });
      } else {
        // getting a subsequent page means we are appending new results to the previous results
        this.setState((prevState) => {
          // Carry the just-loaded page's user offset (index) and moreItems so the next page advances
          // from the correct user position and paging halts when the user list is exhausted; total is
          // the stable grand user total.
          const combinedEffectivePermissions = new ListResult(
            prevState.effectivePermissions.items.concat(updatedEffectivePermissions.items),
            updatedEffectivePermissions.index,
            updatedEffectivePermissions.total,
            undefined,
            updatedEffectivePermissions.moreItems
          );
          return { effectivePermissions: combinedEffectivePermissions, };
        }, () => {
          markRequestComplete();
          // then need to fetch group membership for users in updatedEffectivePermissions
          this.updateUsersGroupMembership(updatedEffectivePermissions, true);
        });
      }
    };

    InProgressRequestsTracker.markRequestInProgress(page.startIndex);
    if (this.isSessionSiteOrServerAdmin()) {
      // If current user is an admin then we need to show site role in effective permission grid
      EffectivePermissions.getEffectivePermissionsWithSiteRole(
        effectivePermissionsFetchParams,
        this.authorizableTypeIds())
        .then(setEffectivePermissionInState);
    } else {
      EffectivePermissions.getEffectivePermissions(effectivePermissionsFetchParams, this.authorizableTypeIds())
        .then(setEffectivePermissionInState);
    }
  };

  private updateUsersGroupMembership(updatedEffectivePermissions: ListResult<IGranteePermissions>, appendResults?: boolean): void {
    const userIds = updatedEffectivePermissions.items.map((item: IGranteePermissions) => item.user.id);
    if (userIds.length) {
      // get the ids of the groups that each user is a member of
      WebHttpClient.sendRequest(new GetUsersGroupMembershipRequest({ userIds }))
        .then((result: IGetUsersGroupMembershipResult) => {
          let updatedUsersGroupMembership: _.Dictionary<string[]> = {};
          result.members.forEach((member: IGroupMember) => {
            let userGroups = updatedUsersGroupMembership[member.userId];
            if (!userGroups) {
              updatedUsersGroupMembership[member.userId] = [member.groupId];
            } else {
              userGroups.push(member.groupId);
            }
          });
          this.explicitGrid && this.explicitGrid.setRelevantGroupMembership(updatedUsersGroupMembership, appendResults);
        });
    } else {
      this.explicitGrid && this.explicitGrid.setRelevantGroupMembership(undefined, appendResults);
    }
  }

  // This method coordinates the interactions between explicit permissions grid and effective permissions grid
  // whenever a row is selected from either grid. However, there are few different scenarios (see comments below).
  private handleRowSelect = (rowData: PermissionsGridRow, selectedFromEffectivePermissions: boolean, selectedRelevantRule: boolean): void => {
    // Scenario 1: a user is selected from effective permissions grid; this should trigger highlighting of
    // permission rules relevant to that user in the explicit permissions grid.
    if (selectedFromEffectivePermissions) {
      this.explicitGrid?.setRelevantUserId(rowData?.granteeId);
      return;
    }

    // Scenario 2: rowData is missing indicates there should be no selected row anymore.
    if (!rowData) {
      this.effectiveGrid?.clearSelectedRow();

      // Scenario 2.5: all permission rules have been deleted, and need to reset other grid data as well.
      if (!selectedRelevantRule) {
        this.explicitGrid?.setRelevantUserId(undefined);
        this.setState({ effectivePermissions: null });
      }
      return;
    }

    // Scenario 3: a normal permission rule is selected from explicit permissions grid; this should trigger
    // an update of effective permissions grid.
    this.effectivePermissionsFilterClauses = this.getFilterClausesForEffectivePermissionsOnRowSelection(rowData);
    this.updateEffectivePermissions(this.effectivePermissionsFilterClauses, true);
  };

  private getNextPermissionsPage = (): void => {
    this.updateEffectivePermissions(this.effectivePermissionsFilterClauses, false);
  };

  // We want to display a notice in the success toast that the changes can have a delay for the following scenarios
  // 1) The permissions they are changing contain Databases or Tables while outside of a container
  // 2) The permissions they are changing contain Databases while inside a container e.g. projects.
  private useSuccessWithWaitMessage = (permissions: IGranteePermissions): boolean => {
    if (isAuthorizableTypeCatalog(this.props.authorizableType)) {
      return true;
    }

    const showsProjectDatabaseTemplate = permissions.permissionsByContentType &&
      permissions.permissionsByContentType[AuthorizableType.PROJECT_DATABASE_TEMPLATE] != null &&
      !_.isEmpty(permissions.permissionsByContentType[AuthorizableType.PROJECT_DATABASE_TEMPLATE]);
    const showsProjectTableTemplate = permissions.permissionsByContentType &&
      permissions.permissionsByContentType[AuthorizableType.PROJECT_TABLE_TEMPLATE] != null &&
      !_.isEmpty(permissions.permissionsByContentType[AuthorizableType.PROJECT_TABLE_TEMPLATE]);

      return showsProjectDatabaseTemplate || showsProjectTableTemplate;
  };

  private saveExplicitPermissionEdits = (permissions: IGranteePermissions): Promise<void | IResult> => {
    // server request to set the provided permissions for the authorizables
    return setExplicitPermissionsForAuthorizables({ type: this.props.authorizableType, ids: this.state.authorizables.keys() }, permissions)
      .then((result: IResult) => {
        this.updateExplicitPermissionsAndEditability().then(() => {
          const granteeName = L10n.elideIfLong(PermissionsHelper.granteeName(permissions));
          if (result.errors && result.errors.length) {
            ToasterService.instance.pop({
              type: ToastType.Error,
              text: Messages.permissionRuleChangeFailed({ GRANTEE_NAME: granteeName, }),
            });
          } else {
            ToasterService.instance.pop({
              type: ToastType.Info,
              text: this.useSuccessWithWaitMessage(permissions)
                ? Messages.permissionRuleChangedSuccessWithWait({ GRANTEE_NAME: granteeName, })
                : Messages.permissionRuleChangedSuccess({ GRANTEE_NAME: granteeName, }),
            });

            if (this.props.authorizableType === AuthorizableType.COLLECTION) {
              // Due to slowness in BE updates we optimistically update collection visibility label in redux
              // after we've successfully updated permissions.
              // Reminder private just means there are no explicit permissions set.
              const visibility = (this.state.explicitPermissions == null || this.state.explicitPermissions.items.length === 0) ? CollectionVisibility.private : CollectionVisibility.public;
              this.props.setCollectionVisibility(this.state.authorizables.keys(), visibility);
            }
          }
        });
      });
  };

  private updateLockState = (newLockState: LockUnlockPermissionsState, newNestedProjectsState?: NestedProjectsPermissionsState): void => {
    this.setState((state) => {
      const updatedControlledPermissions: boolean = newLockState === LockUnlockPermissionsState.Locked;
      const updatedNestedProjectsPermissions: boolean = newNestedProjectsState === NestedProjectsPermissionsState.Included;
      let authorizables: Set<any> = state.authorizables.clone();
      if (this.props.authorizableType === AuthorizableType.PROJECT) {
        authorizables.each((project: IProjectInfo) => {
          if (project.controlledPermissionsEnabled !== updatedControlledPermissions) {
            project.controlledPermissionsEnabled = updatedControlledPermissions;
          }
          project.nestedProjectsPermissionsIncluded = updatedNestedProjectsPermissions;
        });
      } else if (this.props.authorizableType === AuthorizableType.DATABASE) {
        authorizables.each((database: any) => {
          if (database.controlledPermissionsEnabled !== updatedControlledPermissions) {
            database.controlledPermissionsEnabled = updatedControlledPermissions;
          }
        });
      }

      return { authorizables, };
    });
  };

  private handleEditModeChange = (isEditingPermissionRule: boolean): void => {
    this.setState({ isEditingPermissionRule, });
  };

  private setExplicitGridRef = (element: PermissionsGrid): void => {
    this.explicitGrid = element;
  };

  private setEffectiveGridRef = (element: PermissionsGrid): void => {
    this.effectiveGrid = element;
  };

  private renderPermissionsContent = (
    permissions: ListResult<IGranteePermissions>,
    authorizableType: AuthorizableType,
    isExplicitPermissions: boolean,
    containerAuthorizableType?: AuthorizableType
  ): JSX.Element => {
    return (
      <PermissionsGrid
        ariaLabelledBy={isExplicitPermissions ? EXPLICIT_PERMISSIONS_TESTID : EFFECTIVE_PERMISSIONS_TESTID}
        permissions={permissions}
        permissionRoleHelper={this.permissionRoleHelper}
        capabilitySections={this.capabilitySectionsByAuthorizableType[authorizableType]}
        authorizableType={authorizableType}
        containerAuthorizableType={containerAuthorizableType}
        isExplicitPermissions={isExplicitPermissions}
        isSessionSiteOrServerAdmin={this.isSessionSiteOrServerAdmin()}
        isEditable={isExplicitPermissions ? this.state.editability.editable : null}
        editabilityDescription={isExplicitPermissions ? this.state.editability.editabilityDescription : null}
        unspecifiedPermissions={isExplicitPermissions ? this.unspecifiedPermissions : null}
        onRowSelect={this.handleRowSelect}
        savePermissionEdits={this.saveExplicitPermissionEdits}
        onEditModeChange={isExplicitPermissions ? this.handleEditModeChange : null}
        disableNonEditingRows={this.state.isEditingPermissionRule}
        selectedFromSearch={isExplicitPermissions && this.state.ruleSelectedFromSearch}
        ref={isExplicitPermissions ? this.setExplicitGridRef : this.setEffectiveGridRef}
        getNextPermissionsPage={!isExplicitPermissions && this.getNextPermissionsPage}
        getSearchTextBoxText={this.getSearchTextBoxText}
        clearSearchTextBoxText={this.clearSearchTextBoxText}
        isUserVisibilityLimited={this.props.isUserVisibilityLimited}
      />
    );
  };

  /**
   * Public - only for testing purpose
   */
  public onSearchTextForEffectivePermissionChange = (searchText: string) => {
    this.explicitGrid?.clearSelectedRow();

    this.effectivePermissionsFilterClauses = this.getFilterClausesForEffectivePermissionsOnSearchTextChange(searchText);
    this.updateEffectivePermissions(this.effectivePermissionsFilterClauses, true);
  };

  private debounceOnSearchTextChange = _.debounce(this.onSearchTextForEffectivePermissionChange,
    this.InputDebounceMilliseconds);

  private setSelectedAuthorizableTypeIndex = (index: number) => {
    this.setState({ selectedAuthorizableTypeIndex: index, });
  };

  private renderAuthorizableTypeSelector = (): JSX.Element => {
    if (isAuthorizableTypeContainer(this.props.authorizableType) && !this.isDatabaseInProject()) {
      // permissions content is outside of TabsWidget for styling reasons (grid extends beyond the tabs)
      // and logic reasons (the grid knows all of the permissions across all authorizable types, but
      // internally chooses which to display based on selected tab)
      return (
        <AuthorizableTypeSelector
          totalAuthorizableTypes={this.totalAuthorizableTypes}
          onSelectAuthorizableType={this.setSelectedAuthorizableTypeIndex}
        />
      );
    }
  };

  private renderExplicitPermissions = (): JSX.Element => {
    if (isAuthorizableTypeContainer(this.props.authorizableType) && !this.isDatabaseInProject()) {
      // permissions content is outside of TabsWidget for styling reasons (grid extends beyond the tabs)
      // and logic reasons (the grid knows all of the permissions across all authorizable types, but
      // internally chooses which to display based on selected tab)
      return this.renderPermissionsContent(this.state.explicitPermissions, this.totalAuthorizableTypes[this.state.selectedAuthorizableTypeIndex], true, this.props.authorizableType);
    } else {
      return this.renderPermissionsContent(this.state.explicitPermissions, this.props.authorizableType, true);
    }
  };

  private renderEffectivePermissions = (): JSX.Element => {
    if (isAuthorizableTypeContainer(this.props.authorizableType) && !this.isDatabaseInProject()) {
      return this.renderPermissionsContent(this.state.effectivePermissions, this.totalAuthorizableTypes[this.state.selectedAuthorizableTypeIndex], false);
    } else {
      return this.renderPermissionsContent(this.state.effectivePermissions, this.props.authorizableType, false);
    }
  };

  private renderTitleHeader = (text: string, disabled: boolean, isTopmostComponent?: boolean, id?: string): JSX.Element => {
    let className;
    if (disabled) {
      if (isTopmostComponent) {
        className = PermissionsDialogStyles.TitleHeaderClassTopDisabled;
      } else {
        className = PermissionsDialogStyles.TitleHeaderClassDisabled;
      }
    } else {
      if (isTopmostComponent) {
        className = PermissionsDialogStyles.TitleHeaderClassTop;
      } else {
        className = PermissionsDialogStyles.TitleHeaderClass;
      }
    }

    // the elements above the title use float, so need 2 divs to properly set whitespace above the title
    return (
      <div className={className}>
        <div  id={id}>
          {text}
        </div>
      </div>
    );
  };

  private setPermissionsSearchTextBoxRef = (element: HTMLInputElement): void => {
    this.searchTextBoxRef = element;
  };

  public clearSearchTextBoxText = (): void => {
    this.searchTextBoxRef.value && (this.searchTextBoxRef.value = '');
  };

  public getSearchTextBoxText = (): string => {
    return this.searchTextBoxRef.value;
  };

  private renderEffectivePermissionsSearch = (): JSX.Element => {
    const searchFieldProps: TextFieldProps = {
      kind: 'search',
      label: Messages.searchUserToViewEffectivePermission(),
      onChange: (e) => this.debounceOnSearchTextChange(e.target.value),
      testId: PermissionsDialog.PERMISSIONS_SEARCH_TEXTBOX_TESTID,
      className: PermissionsDialogStyles.SearchTextboxContainer,
    };

    return (
      <TextFieldWidget
        {...searchFieldProps}
        aria-describedby='autoSuggestDesc'
        disabled={this.state.isEditingPermissionRule}
        ref={this.setPermissionsSearchTextBoxRef}
      />
    );
  };

  // The additional messages beyond the lock/unlock message needs to be shown only in certain conditions:
  // - When database is mot assigned to project
  // - When Database is assigned and multiple databases are selected and the project state is mixed
  private shouldRenderAdditionalMessage = (): boolean => {
    if(this.props.authorizableType === AuthorizableType.DATABASE) {
      if(!this.isDatabaseInProject()) {
        return true;
      }
      const projectLockState = this.getProjectLockState();
      if(projectLockState === LockUnlockPermissionsState.Unlocked) {
        return false;
      }
      if(this.props.authorizables.size() === 1 && projectLockState === LockUnlockPermissionsState.Locked) {
        return false;
      }
      if(projectLockState === LockUnlockPermissionsState.Mixed) {
        return true;
      }
    }
    return false;
  };

  private shouldRenderPermissionsLockUnlock = (): boolean => {
    // Only render the PermissionsLockUnlock component if authorizableType is project or database,
    // or if there is a message for non container authorizableType to display (e.g.,
    // when the parent project permissions are locked and the message might say "Locked to parent project")

    // The permissions lock unlock message should only be shown for certain cases if the authorizable type is Database
    let shouldRenderPermissionsLockUnlock = (isAuthorizableTypeContainer(this.props.authorizableType)) || this.state.editability.nonContainerMessage !== '';
    if(this.props.authorizableType === AuthorizableType.DATABASE) {
      const projectLockState = this.getProjectLockState();
      if(this.isDatabaseInProject()) {
        if(this.props.authorizables.size() > 1 && projectLockState === LockUnlockPermissionsState.Locked) {
          shouldRenderPermissionsLockUnlock = false;
        } else if(projectLockState === LockUnlockPermissionsState.Unlocked) {
          shouldRenderPermissionsLockUnlock = false;
        } else if(projectLockState === LockUnlockPermissionsState.Mixed) {
          shouldRenderPermissionsLockUnlock = false;
        }
      }
    }
    return shouldRenderPermissionsLockUnlock; //(isAuthorizableTypeContainer(this.props.authorizableType)) || this.state.editability.nonContainerMessage !== '';
  };

  private renderPermissionsLockUnlock = (): JSX.Element => {
    return <PermissionsLockUnlock
      authorizables={this.state.authorizables}
      authorizableType={this.props.authorizableType}
      nonContainerMessage={this.state.editability.nonContainerMessage}
      icon={this.state.editability.icon}
      sourceAuthorizablesById={this.state.sourceAuthorizablesById}
      updateLockStatus={this.updateLockState}
    />;
  };

  private renderHelpLink = (): JSX.Element => {
    return (
      <TextLinkWidget
        className={PermissionsDialogStyles.HelpLink}
        href={HelpService.getServerHelpUrlByCategory('permissions')}
        rel="noopener noreferrer"
        target="_blank"
        testId={PERMISSIONS_HELP_TESTID}
      >
        {Messages.learnMoreAboutPermissions()}
      </TextLinkWidget>
    );
  };

  private calculateDialogHeight = (): number => {
    // unknown at the time of first opening the dialog if we need to show the lock message, so just always include
    // it in the height calculation so the dialog centering does not get ruined when the height changes
    let lockMessage = PermissionsLockUnlockStyles.permissionsLockUnlockHeight + PermissionsDialogStyles.titleMarginTop;
    let additionalMessage = PermissionsDialogStyles.lineHeight + 5;
    let permissionsRulesHeader = PermissionsDialogStyles.lineHeight + PermissionsDialogStyles.titleMarginBottom; // permission rules header
    let sectionAboveGrids = lockMessage + permissionsRulesHeader;
    if(this.shouldRenderAdditionalMessage()) {
      sectionAboveGrids += additionalMessage;
    }
    if (isAuthorizableTypeContainer(this.props.authorizableType)) {
      sectionAboveGrids += PermissionsDialogStyles.tabsHeight + PermissionsDialogStyles.tabsBottomHeight + PermissionsDialogStyles.tabsMarginBottom;
    }
    const gridHeight = PermissionsGridStyles.RowHeight * (PermissionsGridStyles.MaxRows + 1) + 2;

    // also unknown when first opening the dialog is if the grid is editable, so need to always
    // include the height of the add new permissions button, which only appears if it is editable
    let addNewPermissionsHeight = AddNewPermissionStyles.ButtonTopMargin + TabStyles.Sizing.RowHeight; // button widget uses rowheight for its height
    let sectionBetweenGrids = addNewPermissionsHeight + PermissionsDialogStyles.titleMarginTop + PermissionsDialogStyles.lineHeight + PermissionsDialogStyles.searchMarginTop + 24 + PermissionsDialogStyles.searchMarginBottom + 18;
    return sectionAboveGrids + gridHeight + sectionBetweenGrids + gridHeight;
  };

  private isDatabaseInProject()
  {
    return LockStatusHelper.isDatabaseInProject(this.props.authorizableType, this.props.authorizables);
  }

  // Get the projects lock state for data bases
  private getProjectLockState = (): LockUnlockPermissionsState => {
    const isDatabase = this.props.authorizableType === AuthorizableType.DATABASE;
    const dbsInProjectsPostMVP = getSessionManagementServiceInstance().databasesInProjectsPostMVPEnabled();
    if(dbsInProjectsPostMVP && isDatabase && !_.isEmpty(this.state.sourceAuthorizablesById)) {
      // Find if all the projects are locked:
      const projectLocked = Object.values(this.state.sourceAuthorizablesById).every(value => {
        return value.type === AuthorizableType.PROJECT && value.controllingPermissionsEnabled === true;
      });
      if(projectLocked) {
        return LockUnlockPermissionsState.Locked;
      }
      // check to see if atleast one of the projects is locked
      const projectMixed = Object.values(this.state.sourceAuthorizablesById).filter(value => {
        return value.type === AuthorizableType.PROJECT && value.controllingPermissionsEnabled === true;
      }).length > 0;
      if(projectMixed) {
        return LockUnlockPermissionsState.Mixed;
      }
    }
    return LockUnlockPermissionsState.Unlocked;
  };

  private renderAdditionalMessage = (): JSX.Element => {
    const dbIsAssigned = this.isDatabaseInProject();

    // Multiple databases have been selected;
    const multi = this.props.authorizables.size() > 1;

    const lockState = LockStatusHelper.getLockState(this.props.authorizables);

    // Multiple databases selected - assigned and unassigned
    const mixed = lockState === LockUnlockPermissionsState.Mixed || lockState === LockUnlockPermissionsState.Unlocked;

    // Decide the message to be shown. If the database is assigned to a project, then check if there are
    // locked and unlocked projects, and if so show a message with a mixed message, else show no message
    // In case all the databases selected are unassigned, then show the message based on whether one or
    // more databases are selected
    const message = dbIsAssigned ?
                      (mixed ? DatabaseAdditionalMessages.mixedMessage() : '') :
                        (multi ? DatabaseAdditionalMessages.multiMessage() : DatabaseAdditionalMessages.singleMessage());

    return (
        <div className={PermissionsDialogStyles.DatabaseUnAssignedMessage}><span> { message } </span></div>
    );
  };

  public render() {
    const shouldRenderPermissionsLockUnlock = this.shouldRenderPermissionsLockUnlock();

    const shouldRenderAdditionalMessage = this.shouldRenderAdditionalMessage();

    // height has to be set here to a number so that when the dialog first opens it is properly centered
    const height = this.calculateDialogHeight();

    return (
      <div className={PermissionsDialogStyles.DialogContents}
        style={{ height, maxHeight: height }}
        ref={this.dialogRef}>
        {shouldRenderPermissionsLockUnlock && this.renderPermissionsLockUnlock()}
        {this.renderHelpLink()}
        {shouldRenderAdditionalMessage && this.renderAdditionalMessage()}
        {isAuthorizableTypeContainer(this.props.authorizableType) && this.renderAuthorizableTypeSelector()}
        {this.renderTitleHeader(Messages.permissionRules(), false, !shouldRenderPermissionsLockUnlock && !shouldRenderAdditionalMessage, EXPLICIT_PERMISSIONS_TESTID)}
        {this.renderExplicitPermissions()}
        {this.renderTitleHeader(Messages.effectivePermissions(), this.state.isEditingPermissionRule, false, EFFECTIVE_PERMISSIONS_TESTID)}
        <span className="sr-only" aria-hidden="true" id="autoSuggestDesc">{Messages.effectivePermissionsScreenReaderHelpMsg()}</span>
        {this.renderEffectivePermissionsSearch()}
        {this.renderEffectivePermissions()}
      </div>
    );
  }
}

const mapDispatchToProps: MapDispatchToProps<PermissionsDialogDispatchProps> = (dispatch) => bindActionCreators({
  showRouterError,
  setCollectionVisibility
}, dispatch);

const mapStateToProps: MapStateToProps<PermissionsDialogSelectorProps> = (state: AppState) => ({
  catalogingEnabled: selectCatalogingOnSiteEnabled(state),
  einsteinCopilotForTableauEnabled: SessionSiteSelectors.isEinsteinCopilotForTableauEnabled(state),
  isUserVisibilityLimited: isUserVisibilityLimited(state.session.info.site.userVisibility),
});

export const ConnectedPermissionsDialog: React.ComponentClass<PermissionsDialogOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(PermissionsDialog);
